import React, { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import { componentWithFG } from '@atlaskit/platform-feature-flags-react/component-with-fg';
import { useGlobalShortcutsDataRelay } from '@atlassian/global-shortcuts';
import GlobalShortcutsRelayClient from '@atlassian/global-shortcuts/utils/GlobalShortcutsRelayClient';
import type { ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import { useNavBoundaryContext } from '@atlassian/navigation-error-boundary';

import type {
	GlobalSideNavigationProps,
	ItemsConfig,
	NetworkStorageParams,
} from '../../common/types';
import { useOptions } from '../options-provider';

import { useForYouNavItem } from './use-for-you-nav-item';
import { useHydratedShortcuts } from './use-hydrated-shortcuts';
import { useRecentNavItem } from './use-recent-nav-item';
import { useStarredNavItem } from './use-starred-nav-item';

export type GlobalItemsConfig = Map<keyof ItemsConfig, ItemsConfig[keyof ItemsConfig]>;

export type ItemsAndShortcuts = Pick<
	GlobalSideNavigationProps,
	'growthItemShortcuts' | 'items' | 'shortcuts'
> & {
	allItems: ItemsConfig;
	forYouMenuId: string;
	// todo: Migrate this to ItemsConfig?
	globalItems: GlobalItemsConfig;
};

export const ItemsAndShortcutsContext: React.Context<ItemsAndShortcuts | undefined> = createContext<
	ItemsAndShortcuts | undefined
>(undefined);

export type ItemsAndShortcutsProviderProps = PropsWithChildren<
	Pick<GlobalSideNavigationProps, 'forYou' | 'growthItemShortcuts' | 'items'> & {
		rawShortcuts: GlobalSideNavigationProps['shortcuts'];
	}
>;

type ItemsAndShortcutsProviderWithQueryRefProps = ItemsAndShortcutsProviderProps & {
	queryRef: NonNullable<NetworkStorageParams['queryRef']>;
};

type ItemsAndShortcutsProviderInnerProps = PropsWithChildren<{
	forYouMenuId: string;
	forYouNavItem: ReturnType<typeof useForYouNavItem>;
	growthItemShortcuts: ItemsAndShortcutsProviderProps['growthItemShortcuts'];
	items: ItemsAndShortcutsProviderProps['items'];
	recentNavItem: ReturnType<typeof useRecentNavItem>;
	shortcuts: GlobalSideNavigationProps['shortcuts'];
	starredNavItem: ReturnType<typeof useStarredNavItem>;
}>;

// Provides all items and shortcuts.
// If these items are truly static, they could be folded into `OptionsProvider` instead.
// If any items are not static, they may need their own context to avoid impacting the consumers of the other items.
const ItemsAndShortcutsProviderHydrated = componentWithFG(
	'navx-4606-gas-in-gsn-agg-hydration',
	ItemsAndShortcutsProviderAGGHydrated,
	ItemsAndShortcutsProviderBFFHydrated,
);

export const ItemsAndShortcutsProvider: React.FC<
	Pick<
		GlobalSideNavigationProps<ObjectListFlyoutItem>,
		'forYou' | 'growthItemShortcuts' | 'items'
	> & {
		rawShortcuts: GlobalSideNavigationProps['shortcuts'];
	} & {
		children?: React.ReactNode | undefined;
	}
> = componentWithFG(
	'navx-4515-jira-gsn-gas-ssr',
	ItemsAndShortcutsProviderSSR,
	ItemsAndShortcutsProviderHydrated,
);

function ItemsAndShortcutsProviderAGGHydrated({
	children,
	forYou,
	growthItemShortcuts,
	items,
	rawShortcuts,
}: ItemsAndShortcutsProviderProps): React.JSX.Element {
	const { app, cloudId, isShortcutsStatic } = useOptions();
	const { handleError } = useNavBoundaryContext({
		location: 'items-and-shortcuts-provider-agg-hydrated',
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		sentryConfig: { product: app },
		disableAnalytics: true,
	});
	const forYouNavItem = useForYouNavItem(forYou);
	const forYouMenuId = forYouNavItem.length ? forYouNavItem[0][0] : '';
	const recentNavItem = useRecentNavItem(forYou);
	const starredNavItem = useStarredNavItem(forYou);

	return (
		<GlobalShortcutsRelayClient
			shortcutsConfig={rawShortcuts}
			cloudId={cloudId}
			isShortcutsStatic={isShortcutsStatic}
			onError={handleError}
			product={app}
		>
			{(shortcuts) => (
				<ItemsAndShortcutsProviderInner
					forYouMenuId={forYouMenuId}
					forYouNavItem={forYouNavItem}
					growthItemShortcuts={growthItemShortcuts}
					items={items}
					recentNavItem={recentNavItem}
					shortcuts={shortcuts}
					starredNavItem={starredNavItem}
				>
					{children}
				</ItemsAndShortcutsProviderInner>
			)}
		</GlobalShortcutsRelayClient>
	);
}

function ItemsAndShortcutsProviderBFFHydrated({
	children,
	forYou,
	growthItemShortcuts,
	items,
	rawShortcuts,
}: ItemsAndShortcutsProviderProps): React.JSX.Element {
	const forYouNavItem = useForYouNavItem(forYou);
	const forYouMenuId = forYouNavItem.length ? forYouNavItem[0][0] : '';
	const recentNavItem = useRecentNavItem(forYou);
	const starredNavItem = useStarredNavItem(forYou);
	const shortcuts = useHydratedShortcuts(rawShortcuts);

	return (
		<ItemsAndShortcutsProviderInner
			forYouMenuId={forYouMenuId}
			forYouNavItem={forYouNavItem}
			growthItemShortcuts={growthItemShortcuts}
			items={items}
			recentNavItem={recentNavItem}
			shortcuts={shortcuts}
			starredNavItem={starredNavItem}
		>
			{children}
		</ItemsAndShortcutsProviderInner>
	);
}

export function useItemsAndShortcuts(): ItemsAndShortcuts {
	const itemsAndShortcuts = useContext(ItemsAndShortcutsContext);

	invariant(
		itemsAndShortcuts,
		'useItemsAndShortcuts must be used inside an initialized ItemsAndShortcutsProvider',
	);

	return itemsAndShortcuts;
}

function ItemsAndShortcutsProviderSSR(props: ItemsAndShortcutsProviderProps): React.JSX.Element {
	const { networkStorageParams } = useOptions();
	const queryRef = networkStorageParams?.queryRef;
	if (queryRef) {
		return <ItemsAndShortcutsProviderWithQueryRef {...props} queryRef={queryRef} />;
	}

	// if no queryRef provided, fallback to the hydrated version for GAS data
	return <ItemsAndShortcutsProviderHydrated {...props} />;
}

function ItemsAndShortcutsProviderWithQueryRef({
	children,
	forYou,
	growthItemShortcuts,
	items,
	rawShortcuts,
	queryRef,
}: ItemsAndShortcutsProviderWithQueryRefProps): React.JSX.Element {
	const { app, cloudId, isShortcutsStatic } = useOptions();
	const forYouNavItem = useForYouNavItem(forYou);
	const forYouMenuId = forYouNavItem.length ? forYouNavItem[0][0] : '';
	const recentNavItem = useRecentNavItem(forYou);
	const starredNavItem = useStarredNavItem(forYou);
	const { handleError } = useNavBoundaryContext({
		location: 'use-ssr-shortcuts',
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		sentryConfig: { product: app },
		disableAnalytics: true,
	});

	const shortcuts = useGlobalShortcutsDataRelay({
		cloudId: cloudId,
		onError: handleError,
		queryRef,
		isShortcutsStatic,
		shortcutsConfig: rawShortcuts,
	});

	return (
		<ItemsAndShortcutsProviderInner
			forYouMenuId={forYouMenuId}
			forYouNavItem={forYouNavItem}
			growthItemShortcuts={growthItemShortcuts}
			items={items}
			recentNavItem={recentNavItem}
			shortcuts={shortcuts}
			starredNavItem={starredNavItem}
		>
			{children}
		</ItemsAndShortcutsProviderInner>
	);
}

function ItemsAndShortcutsProviderInner({
	children,
	forYouMenuId,
	forYouNavItem,
	growthItemShortcuts,
	items,
	recentNavItem,
	shortcuts,
	starredNavItem,
}: ItemsAndShortcutsProviderInnerProps): React.JSX.Element {
	const itemsAndShortcuts = useMemo((): ItemsAndShortcuts => {
		const globalItems: GlobalItemsConfig = new Map([
			...forYouNavItem,
			...recentNavItem,
			...starredNavItem,
		]);
		const allItems: ItemsConfig = { ...Object.fromEntries(globalItems), ...items };
		return {
			allItems,
			forYouMenuId,
			globalItems,
			growthItemShortcuts,
			items,
			shortcuts,
		};
	}, [
		forYouNavItem,
		forYouMenuId,
		recentNavItem,
		starredNavItem,
		items,
		growthItemShortcuts,
		shortcuts,
	]);

	return (
		<ItemsAndShortcutsContext.Provider value={itemsAndShortcuts}>
			{children}
		</ItemsAndShortcutsContext.Provider>
	);
}
